import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  Check,
  Circle,
  ClipboardList,
  Clock3,
  MapPin,
  MoreHorizontal,
  Save,
  Search,
  Send,
  ShieldCheck,
  Undo2,
  UserX,
  UsersRound,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { UserAvatar } from '../../components/UserAvatar';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { TalentProfile } from '../../types/domain';

type ReviewAction = 'reject' | 'suspend';
type TalentStatus = TalentProfile['verification_status'];

interface TalentDraft {
  displayName: string;
  city: string;
  languages: string;
  skills: string;
  categories: string;
  bio: string;
  isAvailable: boolean;
  isSearchable: boolean;
}

const emptyDraft: TalentDraft = {
  displayName: '',
  city: 'Lahore',
  languages: '',
  skills: '',
  categories: '',
  bio: '',
  isAvailable: true,
  isSearchable: true,
};

function listText(values: string[]) {
  return values.join(', ');
}

function parseList(value: string) {
  return [...new Set(value.split(',').map((item) => item.trim()).filter(Boolean))];
}

function draftFromProfile(profile: TalentProfile): TalentDraft {
  return {
    displayName: profile.display_name,
    city: profile.city,
    languages: listText(profile.languages),
    skills: listText(profile.skills),
    categories: listText(profile.categories),
    bio: profile.bio,
    isAvailable: profile.is_available,
    isSearchable: profile.is_searchable,
  };
}

function TalentCard({ canAssign, profile }: { canAssign: boolean; profile: TalentProfile }) {
  const verified = profile.verification_status === 'verified';
  return (
    <article className={`talent-card${verified ? ' talent-card-verified' : ''}`}>
      <div className="talent-card-heading">
        <UserAvatar className="avatar large" name={profile.display_name} src={profile.avatar} />
        <div>
          <h2>
            {profile.display_name}
            {verified ? <span className="talent-verified-badge" title="BFC verified"><BadgeCheck size={14} /> BFC verified</span> : null}
          </h2>
          <p><MapPin size={14} /> {profile.reference} · {profile.city}</p>
        </div>
        <StatusPill value={profile.verification_status} />
      </div>
      <p className="talent-bio">{profile.bio || 'No biography has been added yet.'}</p>
      <div className="tag-list">
        {[...profile.skills, ...profile.categories].slice(0, 5).map((tag) => <span key={tag}>{tag}</span>)}
        {profile.skills.length + profile.categories.length === 0 ? <span>No skills listed</span> : null}
      </div>
      <small>
        {profile.is_assignment_eligible
          ? `Eligible for assignments · ${profile.is_available ? 'Available' : 'Marked unavailable'}`
          : 'Not eligible for assignments'}
      </small>
      {canAssign && profile.is_assignment_eligible ? (
        <div className="talent-card-actions">
          <Link className="button secondary" to={`/assignments?talent=${encodeURIComponent(profile.reference)}`}><ClipboardList size={15} /> Assign to campaign</Link>
        </div>
      ) : null}
    </article>
  );
}

export function TalentPage() {
  const { accessToken, capabilities, isPlatformAdmin, profile: account, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<TalentStatus | 'all'>('all');
  const [draftState, setDraft] = useState<TalentDraft | null>(null);
  const [reviewing, setReviewing] = useState<{ profile: TalentProfile; action: ReviewAction } | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const isPersonalWorkspace = selectedWorkspace?.type === 'personal';
  const canAssign = capabilities.includes('assignments:manage') && !isPersonalWorkspace;
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);
  useEffect(() => {
    if (!openActionMenu) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenActionMenu(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [openActionMenu]);
  const talentParams = new URLSearchParams({
    ordering: isPlatformAdmin
      ? 'display_name'
      : 'verification_rank,availability_rank,display_name',
    page: String(page),
    page_size: '20',
  });
  if (debouncedSearch) {
    talentParams.set(/^BA-\d+$/i.test(debouncedSearch) ? 'reference' : 'search', debouncedSearch);
  }
  if (isPlatformAdmin && statusFilter !== 'all') {
    talentParams.set('verification_status', statusFilter);
  }
  const talentPath = isPersonalWorkspace ? '/talent/?mine=true' : `/talent/?${talentParams.toString()}`;
  const talent = useApiCollection<TalentProfile>('talent-onboarding', talentPath, accessToken);
  const rows = useMemo(() => talent.data?.results ?? [], [talent.data?.results]);
  const ownProfile = rows.find((profile) => profile.user_id === account?.id) ?? null;
  const isPersonalTalent = Boolean(
    account?.isBrandAmbassador
    && ownProfile
    && isPersonalWorkspace,
  );
  const draft = draftState ?? (ownProfile ? draftFromProfile(ownProfile) : emptyDraft);

  const refreshTalent = async () => {
    await queryClient.invalidateQueries({ queryKey: ['collection', 'talent-onboarding'] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!ownProfile) throw new Error('Your Brand Ambassador profile is unavailable.');
      return apiRequest<TalentProfile>(`/talent/${ownProfile.id}/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'PATCH',
        body: JSON.stringify({
          display_name: draft.displayName.trim(),
          city: draft.city.trim(),
          languages: parseList(draft.languages),
          skills: parseList(draft.skills),
          categories: parseList(draft.categories),
          bio: draft.bio.trim(),
          is_available: draft.isAvailable,
          is_searchable: draft.isSearchable,
        }),
      });
    },
    onSuccess: async () => {
      setDraft(null);
      await refreshTalent();
    },
  });

  const transitionMutation = useMutation({
    mutationFn: ({ id, action, note = '' }: { id: string; action: string; note?: string }) => apiRequest<TalentProfile>(`/talent/${id}/${action}/`, {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
    onSuccess: async () => {
      setReviewing(null);
      setReviewNote('');
      await refreshTalent();
    },
  });

  function openReview(profile: TalentProfile, action: ReviewAction) {
    transitionMutation.reset();
    setOpenActionMenu(null);
    setReviewNote('');
    setReviewing({ profile, action });
  }

  const profiles = useMemo(() => {
    if (isPlatformAdmin) return rows;
    return [...rows].sort((left, right) => (
      Number(right.verification_status === 'verified') - Number(left.verification_status === 'verified')
      || Number(right.is_available) - Number(left.is_available)
      || left.display_name.localeCompare(right.display_name)
      || left.reference.localeCompare(right.reference)
    ));
  }, [isPlatformAdmin, rows]);
  const pageCount = Math.max(1, Math.ceil((talent.data?.count ?? 0) / 20));

  if (isPersonalTalent && ownProfile) {
    const locked = ownProfile.verification_status === 'pending' || ownProfile.verification_status === 'suspended';
    return (
      <>
        <header className="page-heading">
          <div>
            <p className="eyebrow">Brand Ambassador onboarding · {ownProfile.reference}</p>
            <h1>My talent profile</h1>
            <p>Complete your professional details and submit them to the BFC verification team.</p>
          </div>
          <StatusPill value={ownProfile.verification_status} />
        </header>

        {ownProfile.review_note ? (
          <div className={ownProfile.verification_status === 'verified' ? 'capability-note' : 'form-error'} role="status">
            <strong>Review note:</strong> {ownProfile.review_note}
          </div>
        ) : null}

        <div className="talent-onboarding-layout">
          <section className="content-card management-panel">
            <div className="card-heading">
              <div><h2>Professional profile</h2><p>Comma-separate multiple languages, skills, and categories.</p></div>
            </div>
            <form className="management-form" onSubmit={(event) => { event.preventDefault(); saveMutation.mutate(); }}>
              <div className="form-grid profile-form-grid">
                <label>Display name<input disabled={locked} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} required value={draft.displayName} /></label>
                <label>City<input disabled={locked} onChange={(event) => setDraft({ ...draft, city: event.target.value })} required value={draft.city} /></label>
                <label>Languages<input disabled={locked} onChange={(event) => setDraft({ ...draft, languages: event.target.value })} placeholder="Urdu, English" required value={draft.languages} /></label>
                <label>Skills<input disabled={locked} onChange={(event) => setDraft({ ...draft, skills: event.target.value })} placeholder="Sampling, product demonstration" required value={draft.skills} /></label>
                <label className="wide-field">Product categories<input disabled={locked} onChange={(event) => setDraft({ ...draft, categories: event.target.value })} placeholder="Beauty, food, beverages" required value={draft.categories} /></label>
                <label className="wide-field">Biography<textarea disabled={locked} onChange={(event) => setDraft({ ...draft, bio: event.target.value })} required rows={5} value={draft.bio} /></label>
                <label className="checkbox-field"><input checked={draft.isAvailable} disabled={locked} onChange={(event) => setDraft({ ...draft, isAvailable: event.target.checked })} type="checkbox" /> Available for assignments</label>
                <label className="checkbox-field"><input checked={draft.isSearchable} disabled={locked} onChange={(event) => setDraft({ ...draft, isSearchable: event.target.checked })} type="checkbox" /> Visible in the talent pool</label>
              </div>
              {ownProfile.verification_status === 'verified' ? <p className="field-help">Changing verified professional or identity details returns the profile to draft for another review.</p> : null}
              {locked ? <p className="field-help">{ownProfile.verification_status === 'pending' ? 'Withdraw the submission before editing.' : 'Contact BFC support about this suspended profile.'}</p> : null}
              {saveMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(saveMutation.error)}</p> : null}
              {saveMutation.isSuccess ? <p className="form-success" role="status"><BadgeCheck size={16} /> Profile saved.</p> : null}
              {!locked ? <div className="form-actions"><button className="button primary" disabled={saveMutation.isPending} type="submit">{saveMutation.isPending ? <InlineSpinner /> : <Save size={16} />} {saveMutation.isPending ? 'Saving…' : 'Save professional profile'}</button></div> : null}
            </form>
          </section>

          <aside className="content-card onboarding-checklist-card">
            <div className="card-heading"><div><h2>Verification readiness</h2><p>{ownProfile.onboarding.completed} of {ownProfile.onboarding.total} required items complete</p></div><strong>{ownProfile.onboarding.percentage}%</strong></div>
            <div className="progress-track"><span style={{ width: `${ownProfile.onboarding.percentage}%` }} /></div>
            <div className="talent-checklist">
              {ownProfile.onboarding.items.map((item) => <span className={item.complete ? 'complete' : ''} key={item.key}>{item.complete ? <Check size={15} /> : <Circle size={15} />} {item.label}</span>)}
            </div>
            <p className="field-help">CNIC and display picture are managed under <Link to="/profile">Personal Details</Link>.</p>
            {transitionMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(transitionMutation.error)}</p> : null}
            {ownProfile.onboarding.can_submit ? (
              <button className="button primary full" disabled={transitionMutation.isPending} onClick={() => transitionMutation.mutate({ id: ownProfile.id, action: 'submit' })} type="button"><Send size={16} /> Submit for verification</button>
            ) : null}
            {ownProfile.verification_status === 'pending' ? (
              <button className="button secondary full" disabled={transitionMutation.isPending} onClick={() => transitionMutation.mutate({ id: ownProfile.id, action: 'withdraw' })} type="button"><Undo2 size={16} /> Withdraw submission</button>
            ) : null}
            {ownProfile.is_assignment_eligible ? <p className="verification-success"><Check size={20} /> Your account is eligible for assignments.</p> : null}
            {ownProfile.verification_status === 'verified' ? <p className="verification-success"><ShieldCheck size={20} /> Your complete profile is BFC verified.</p> : null}
            {ownProfile.verification_status === 'draft' && !ownProfile.onboarding.is_complete ? <p className="verification-waiting"><Clock3 size={18} /> Complete every checklist item before submission.</p> : null}
          </aside>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>{isPlatformAdmin ? 'Talent verification' : 'Talent'}</h1>
          <p>{isPlatformAdmin ? 'Review BA submissions and protect the quality of the verified talent pool.' : 'Search shared Brand Ambassadors with a verified email and visible profile.'}</p>
        </div>
        <span className="record-count"><UsersRound size={16} /> {talent.data?.count ?? 0}</span>
      </header>
      <div className="data-toolbar talent-toolbar">
        <label className="search-field"><Search size={17} /><input aria-label="Search talent" onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search name, BA reference, city, skill…" value={search} /></label>
        {isPlatformAdmin ? (
          <select aria-label="Filter verification status" className="table-select" onChange={(event) => { setStatusFilter(event.target.value as TalentStatus | 'all'); setPage(1); }} value={statusFilter}>
            <option value="all">All statuses</option>
            <option value="pending">Pending review</option>
            <option value="draft">Draft</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="suspended">Suspended</option>
          </select>
        ) : null}
      </div>
      <DataPanel loading={talent.isLoading} error={talent.isError} empty={profiles.length === 0}>
        {isPlatformAdmin ? (
          <section className="talent-review-list">
            {profiles.map((profile) => (
              <article className="content-card talent-review-card" key={profile.id}>
                <div className="talent-review-overview">
                  <UserAvatar className="avatar large" name={profile.display_name} src={profile.avatar} />
                  <div className="talent-review-identity">
                    <h2>{profile.display_name}</h2>
                    <p>{profile.reference} · {profile.email} · {profile.phone || 'No phone'} · {profile.city}</p>
                    <div className="tag-list">{[...profile.languages, ...profile.skills, ...profile.categories].map((tag) => <span key={tag}>{tag}</span>)}</div>
                  </div>
                  <div className="talent-review-summary">
                    <span><strong>{profile.onboarding.percentage}%</strong><small>Profile complete</small></span>
                    <span><strong>{profile.cnic || 'Not provided'}</strong><small>CNIC</small></span>
                    <span><strong>{profile.date_of_birth || 'Not provided'}</strong><small>Date of birth</small></span>
                    <span><strong>{profile.submitted_at ? new Date(profile.submitted_at).toLocaleDateString('en-GB') : 'Not submitted'}</strong><small>Submission</small></span>
                  </div>
                  <StatusPill value={profile.verification_status} />
                </div>
                <div className="talent-review-bio-row">
                  <p>{profile.bio || 'No biography provided.'}</p>
                  <div className="talent-review-bio-actions">
                    {canAssign && profile.is_assignment_eligible ? <Link className="button secondary" to={`/assignments?talent=${encodeURIComponent(profile.reference)}`}><ClipboardList size={16} /> Assign to campaign</Link> : null}
                    {profile.verification_status !== 'suspended' ? (
                      <div className="talent-review-overflow">
                        <button
                          aria-expanded={openActionMenu === profile.id}
                          aria-haspopup="menu"
                          aria-label={`More actions for ${profile.display_name}`}
                          className="icon-button talent-review-more"
                          onClick={() => setOpenActionMenu((current) => current === profile.id ? null : profile.id)}
                          type="button"
                        >
                          <MoreHorizontal size={22} />
                        </button>
                        {openActionMenu === profile.id ? (
                          <>
                            <button aria-label="Close actions menu" className="talent-action-menu-backdrop" onClick={() => setOpenActionMenu(null)} type="button" />
                            <div className="talent-action-menu" role="menu">
                              <button onClick={() => openReview(profile, 'suspend')} role="menuitem" type="button"><UserX size={17} /> Suspend</button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
                {profile.review_note ? <p className="review-note"><strong>Latest review note:</strong> {profile.review_note}</p> : null}
                <div className="talent-review-actions">
                  {profile.verification_status === 'pending' ? <button className="button primary" disabled={transitionMutation.isPending} onClick={() => transitionMutation.mutate({ id: profile.id, action: 'verify', note: 'Profile and identity details verified.' })} type="button"><BadgeCheck size={16} /> Verify</button> : null}
                  {profile.verification_status === 'pending' ? <button className="button secondary" onClick={() => openReview(profile, 'reject')} type="button"><X size={16} /> Reject</button> : null}
                  {profile.verification_status === 'suspended' ? <button className="button secondary" disabled={transitionMutation.isPending} onClick={() => transitionMutation.mutate({ id: profile.id, action: 'restore', note: 'Profile restored to draft for correction.' })} type="button"><Undo2 size={16} /> Restore to draft</button> : null}
                </div>
              </article>
            ))}
          </section>
        ) : <section className="talent-grid">{profiles.map((profile) => <TalentCard canAssign={canAssign} key={profile.id} profile={profile} />)}</section>}
      </DataPanel>
      {!isPersonalWorkspace && pageCount > 1 ? (
        <nav aria-label="Talent pagination" className="talent-pagination">
          <button className="button secondary" disabled={page <= 1 || talent.isFetching} onClick={() => setPage((current) => current - 1)} type="button">Previous</button>
          <span>Page {page} of {pageCount}</span>
          <button className="button secondary" disabled={page >= pageCount || talent.isFetching} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
        </nav>
      ) : null}

      {reviewing ? (
        <div className="confirmation-backdrop" onClick={() => setReviewing(null)} role="presentation">
          <section aria-labelledby="talent-review-dialog-title" aria-modal="true" className="confirmation-dialog" onClick={(event) => event.stopPropagation()} role="dialog">
            <button aria-label="Close review dialog" className="icon-button confirmation-close" onClick={() => setReviewing(null)} type="button"><X size={18} /></button>
            <div className="confirmation-icon"><UserX size={22} /></div>
            <h2 id="talent-review-dialog-title">{reviewing.action === 'reject' ? 'Reject BA profile?' : 'Suspend BA profile?'}</h2>
            <p>Give <strong>{reviewing.profile.display_name}</strong> a clear reason so the next step is understandable.</p>
            <label className="review-note-field">Reason<textarea autoFocus onChange={(event) => setReviewNote(event.target.value)} required rows={4} value={reviewNote} /></label>
            {transitionMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(transitionMutation.error)}</p> : null}
            <div className="confirmation-actions">
              <button className="button secondary" onClick={() => setReviewing(null)} type="button">Cancel</button>
              <button className="button danger" disabled={!reviewNote.trim() || transitionMutation.isPending} onClick={() => transitionMutation.mutate({ id: reviewing.profile.id, action: reviewing.action, note: reviewNote.trim() })} type="button">{transitionMutation.isPending ? <InlineSpinner /> : <UserX size={16} />} {reviewing.action === 'reject' ? 'Reject profile' : 'Suspend profile'}</button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
